import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { getAuthenticatedSession, getDefaultExperience } from 'src/api/authentication';
import { AuthenticatedUserProvider } from 'src/contexts/AuthenticatedUserContext';
import { BreadcrumbProvider } from 'src/contexts/BreadcrumbContext';
import { ConfigProvider } from 'src/contexts/ConfigContext';
import { PersonalTaskProvider } from 'src/contexts/PersonalTaskContext';
import PersonalLayoutWrapper from 'src/layouts/personal';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme } from '@repo/theme';
import { AuthenticatedUser, PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { getCustomerThemeSettings } from 'src/api/admin/settings';
import { getConfig } from 'src/api/config';
import { hardRedirect } from 'src/utils/hardRedirect';
import { useEffect, useMemo } from 'react';
import { identifyActor } from '@repo/shared/analytics';

export const Route = createFileRoute('/personal_')({
	staleTime: 1000 * 60 * 5,
	loader: async () => {
		const authenticatedSession = await getAuthenticatedSession();
		if (!authenticatedSession.user) {
			throw redirect({
				to: '/authentication/login',
				search: {
					redirect: location.href,
				},
			});
		}

		// Check permissions
		const verifyPermission = getVerifyPermission(authenticatedSession.user.permissions || []);
		if (!verifyPermission([PERMISSIONS.personalView])) {
			throw redirect({
				to: '/403',
				search: {
					permissions: 'personalView',
					occurredAt: '/personal',
					method: 'GET',
				},
			});
		}

		// When the 2.0 experience is the default, send users to the new employee app.
		const experience = await getDefaultExperience();
		if (experience === '2.0') {
			await hardRedirect('/app/employee/');
		}

		const [themeSettings, config] = await Promise.all([getCustomerThemeSettings(), getConfig()]);

		return { authenticatedSession, themeSettings, config };
	},
	component: PersonalLayout,
});

function PersonalLayout() {
	const { authenticatedSession, config } = Route.useLoaderData();

	useEffect(() => {
		identifyActor(authenticatedSession?.user);
	}, [authenticatedSession?.user?.id]);

	const hasTaskPermission = useMemo(() => {
		const verifyPermission = getVerifyPermission(authenticatedSession?.user?.permissions || []);
		return verifyPermission(PERMISSIONS.personalTasksDo) || verifyPermission(PERMISSIONS.personalTasksView);
	}, [authenticatedSession]);

	return (
		<ThemeProvider theme={lightTheme}>
			<CssBaseline />
			<AuthenticatedUserProvider authenticatedSession={authenticatedSession}>
				<ConfigProvider>
					<BreadcrumbProvider>
						<PersonalTaskProvider hasTaskPermission={hasTaskPermission}>
							<PersonalLayoutWrapper
								authenticatedUser={(authenticatedSession?.user as AuthenticatedUser) || null}
								featureFlags={config?.featureFlags}
							>
								<Outlet />
							</PersonalLayoutWrapper>
						</PersonalTaskProvider>
					</BreadcrumbProvider>
				</ConfigProvider>
			</AuthenticatedUserProvider>
		</ThemeProvider>
	);
}
